import Player from "@vimeo/player";

interface GalleryElements {
	activeVideoContainer: HTMLElement | null;
	activeVideoTitle: HTMLElement | null;
	activeVideoDescription: HTMLElement | null;
	videoContainers: NodeListOf<HTMLElement> | null;
	categories: NodeListOf<HTMLElement> | null;
}

const GalleryVideo = {
	el: {} as GalleryElements,

	init(): void {
		this.el.activeVideoContainer = document.querySelector(
			".video-gallery--active-video-container",
		);
		this.el.activeVideoTitle = document.querySelector(
			".video-gallery--active-video-title",
		);
		this.el.activeVideoDescription = document.querySelector(
			".video-gallery--active-video-description",
		);
		this.el.videoContainers = document.querySelectorAll(
			".video-gallery__video-container",
		);
		this.el.categories = document.querySelectorAll(".category-nav-item");

		this.bindCategoryClicks();
		this.bindVideoClicks();
	},

	bindCategoryClicks(): void {
		if (!this.el.categories) return;

		for (const category of Array.from(this.el.categories)) {
			category.addEventListener("click", (event: MouseEvent) => {
				const target = event.currentTarget as HTMLElement;
				const categoryTitle = target.getAttribute("data-category");
				this.filterVideos(categoryTitle);

				const url = new URL(window.location.href);
				if (categoryTitle) {
					url.searchParams.set("category", categoryTitle);
				} else {
					url.searchParams.delete("category");
				}
				window.history.pushState({}, "", url);
			});
		}
	},

	bindVideoClicks(): void {
		if (!this.el.videoContainers) return;

		for (const container of Array.from(this.el.videoContainers)) {
			const video = container.querySelector<HTMLIFrameElement>("iframe");

			const titleElement = container.querySelector<HTMLElement>(
				".video-gallery__video-title",
			);
			const title = titleElement?.textContent || "";

			const descriptionElement = container.querySelector<HTMLElement>(
				".video-gallery__video-description",
			);
			const description = descriptionElement?.textContent || "";

			container.addEventListener("click", () => {
				this.moveVideoToActiveContainer(video, title, description);
			});
		}
	},

	filterVideos(category: string | null): void {
		if (!this.el.videoContainers || !this.el.categories) {
			console.warn("Video containers or categories are missing.");
			return;
		}

		for (const video of Array.from(this.el.videoContainers)) {
			const videoCategory = video.getAttribute("data-category");
			video.style.display =
				videoCategory === category || !category ? "block" : "none";
		}

		for (const categoryElement of Array.from(this.el.categories)) {
			if (categoryElement.getAttribute("data-category") === category) {
				categoryElement.classList.add("active");
			} else {
				categoryElement.classList.remove("active");
			}
		}
	},

	moveVideoToActiveContainer(
		video: HTMLIFrameElement | null,
		title: string,
		description: string,
	): void {
		if (!video || !this.el.activeVideoContainer) {
			console.warn("Active video container or video element is missing.");
			return;
		}
		const allIframes = document.querySelectorAll<HTMLIFrameElement>(
			".video-gallery__video iframe",
		);
		for (const iframe of Array.from(allIframes)) {
			const src = iframe.getAttribute("src");
			if (src) {
				iframe.setAttribute("src", this.updateControlsInSrc(src, false));
			}
		}

		const iframeContainer =
			this.el.activeVideoContainer.querySelector<HTMLIFrameElement>(
				".active-video-iframe",
			);
		if (iframeContainer) {
			const activeSrc = this.updateControlsInSrc(
				video.getAttribute("src") || "",
				true,
			);
			iframeContainer.setAttribute("src", activeSrc);
		}

		if (this.el.activeVideoTitle) {
			this.el.activeVideoTitle.textContent = title;
		}

		if (this.el.activeVideoDescription) {
			this.el.activeVideoDescription.textContent = description || "";
		}

		if (iframeContainer) {
			const player = new Player(iframeContainer, {
				controls: true,
				autoplay: false,
				muted: false,
			});

			player.play().catch((error: Error) => {
				console.error("Error playing video:", error);
			});
		}
	},

	updateControlsInSrc(src: string, enableControls: boolean): string {
		const url = new URL(src);
		url.searchParams.set("controls", enableControls ? "1" : "0");
		return url.toString();
	},
};

export default GalleryVideo;
